import { forwardRef, useEffect, useState } from 'react'

import { Locale } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'

import {
  DatePickerInput as DesignSystemDatePickerInput,
  DatePickerInputProps as DesignSystemDatePickerInputProps,
  InputProps,
} from '@cash-design-system/react'
import { regionConfig } from '@cash-web/shared-region-feature-main'
import { useRegion } from '@cash-web/shared-region-ui-context-main'

import { getDateFormat } from '../DateInputElement/DateInputElement'

export interface DatePickerInputProps extends InputProps {
  picker?: DesignSystemDatePickerInputProps['picker']
}

function useDateFnsLocale() {
  const regionCode = useRegion()
  const windowLocale = window?.locale ?? 'en-US'
  const [locale, setLocale] = useState<Locale>(windowLocale === 'es-US' ? es : enUS)

  useEffect(() => {
    if (windowLocale === 'es-US') {
      setLocale(es)
    } else {
      const region = regionCode && regionConfig[regionCode]
      region?.defaultDateFnsLocale().then(({ default: locale }) => setLocale(locale))
    }
  }, [regionCode, windowLocale])

  return locale
}

export const DatePickerInput = forwardRef<HTMLInputElement, DatePickerInputProps>(({ picker, id, ...props }, ref) => {
  const { t } = useTranslation()
  const regionCode = useRegion()
  const locale = useDateFnsLocale()

  const datePickerCopy = {
    locale: locale ?? enUS,
    dropdowns: {
      monthLabel: t('Month', { context: 'Date picker month label' }),
      yearLabel: t('Year', { context: 'Date picker year label' }),
    },
    navigation: {
      nextLabel: t('Go to next month', { context: 'Date picker next month arrow label' }),
      previousLabel: t('Go to previous month', { context: 'Date picker previous month arrow label' }),
    },
  }

  return (
    <DesignSystemDatePickerInput
      {...props}
      id={id ?? ''}
      copy={datePickerCopy}
      getDateFormat={regionCode ? () => getDateFormat(regionCode) : undefined}
      picker={picker}
      ref={ref}
    />
  )
})
